import type {
  TravelPaymentValues,
  TravelPersonalFormValues,
} from "@/D2C/features/travel-quote/redux/type";
import { LOB_IDS } from "@/shared/common/static";
import { LOCALISATION_CONFIG } from "@/shared/config/localisation";
import type { SharedPersonalDetailsState } from "@/shared/redux/reducers/personalDetailsSlice";
import { getAuthSessionIdentifiers } from "@/shared/utils/authSession";

import type { LifePlanDetailsFormValues, LifeUploadedDocumentMap } from "../redux/lifeQuoteSlice";

const DEFAULT_LIFE_OPTIONAL_COVERS = [
  { key: "ADVENTURE_SPORTS", value: "false" },
  { key: "TERRORISM", value: "false" },
  { key: "WINTER_SPORTS", value: "false" },
  { key: "GOLF", value: "false" },
  { key: "COVID", value: "false" },
];

type LifeQuoteRequestBody = {
  personal_details: ReturnType<typeof buildLifePersonalDetailsPayload>;
  life_insurance: ReturnType<typeof buildLifeInsurancePayload>;
  client_id: number | null;
  session_id: number | null;
  lob_id: number;
};

function normalizeNullableNumber(value: string | number | null | undefined) {
  const normalizedValue = value === null || value === undefined ? null : Number(value);

  return normalizedValue === null || Number.isNaN(normalizedValue) ? null : normalizedValue;
}

function normalizeNumericField(value: string | number | null | undefined): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const raw = String(value || "").trim();
  if (!raw) {
    return null;
  }

  const directNumber = Number(raw.replace(/,/g, ""));
  if (!Number.isNaN(directNumber)) {
    return directNumber;
  }

  const digitMatch = raw.match(/\d+/g);
  if (!digitMatch?.length) {
    return null;
  }

  const extracted = Number(digitMatch.join(""));
  return Number.isNaN(extracted) ? null : extracted;
}

function formatDateToIsoLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function parseIsoDate(value: string | null | undefined): Date | null {
  const raw = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return null;
  }

  const [year, month, day] = raw.split("-").map(Number);
  const parsed = new Date(year, (month || 1) - 1, day || 1);

  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== (month || 1) - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }

  return parsed;
}

function extractPolicyTermYears(policyTerm: string | null | undefined): number | null {
  const raw = String(policyTerm || "")
    .trim()
    .toLowerCase();
  if (!raw) {
    return null;
  }

  if (/^\d+$/.test(raw)) {
    const direct = Number(raw);
    return Number.isFinite(direct) && direct > 0 ? direct : null;
  }

  const lastNumberMatch = raw.match(/(\d+)(?!.*\d)/);
  if (!lastNumberMatch) {
    return null;
  }

  const years = Number(lastNumberMatch[1]);
  return Number.isFinite(years) && years > 0 ? years : null;
}

function buildLifePolicyDates(termDetails: LifePlanDetailsFormValues) {
  const startDate = formatDateToIsoLocal(new Date());
  const dob = parseIsoDate(termDetails.lifeAssuredDob);
  const policyTermYears = extractPolicyTermYears(termDetails.policyTerm);

  if (!dob || !policyTermYears) {
    return {
      startDate,
      endDate: null as string | null,
    };
  }

  const endDate = new Date(dob);
  endDate.setFullYear(endDate.getFullYear() + policyTermYears);

  return {
    startDate,
    endDate: formatDateToIsoLocal(endDate),
  };
}

function getPaymentResponsePayload(paymentResponse: any) {
  return paymentResponse?.data && typeof paymentResponse.data === "object"
    ? paymentResponse.data
    : paymentResponse;
}

function buildLifePlanName(quote: any) {
  const insurerPrefix = quote?.insurer?.name
    ?.replace(/insurance|insurer|pjsc|limited|co/gi, "")
    .trim();

  return insurerPrefix ? `${insurerPrefix}-${quote?.plan?.name}` : quote?.plan?.name;
}

function buildLifeSelectedOptionalCovers(additionalInfo: any) {
  return additionalInfo?.optionalCovers;
}

function buildLifeTravellerMoreInfo(
  personalDetails: SharedPersonalDetailsState,
  termDetails: LifePlanDetailsFormValues,
  additionalInfo: any
) {
  return [
    {
      first_name: personalDetails?.firstName || "",
      last_name: personalDetails?.lastName || "",
      date_of_birth: termDetails?.lifeAssuredDob || null,
      gender: termDetails?.lifeAssuredGender || additionalInfo?.gender || "",
      nationality: additionalInfo?.nationality || "",
      cityOfResidence: additionalInfo?.cityOfResidence || "",
      passportNo: additionalInfo?.passportNumber || additionalInfo?.passportNo || "",
      beneficiaryName: additionalInfo?.beneficiaryName || "",
      beneficiaryRelationship:
        additionalInfo?.beneficiaryRelation || additionalInfo?.beneficiaryRelationship || "",
      address: additionalInfo?.address || "",
      sourceOfFunds: additionalInfo?.sourceOfFund || additionalInfo?.sourceOfFunds || "",
      emiratesId: additionalInfo?.emiratesId || "",
      emiratesIdExpiryDate: additionalInfo?.emiratesIdExpiryDate || null,
      emiratesIdIssueDate: additionalInfo?.emiratesIdIssueDate || null,
      passportExpiryDate: additionalInfo?.passportExpiryDate || null,
      passportIssueDate: additionalInfo?.passportIssueDate || null,
    },
  ];
}

function toLifeAttachType(fieldKey: string) {
  const parts = fieldKey.split("_");
  const isAdditionalTravellerDocument = parts[0] === "traveller" && parts.length > 2;
  const travellerIndex = isAdditionalTravellerDocument ? parts[1] : "";
  const documentKey = isAdditionalTravellerDocument ? parts.slice(2).join("_") : fieldKey;
  const formattedDocumentKey = documentKey
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");

  return isAdditionalTravellerDocument
    ? `${formattedDocumentKey}${travellerIndex}`
    : formattedDocumentKey;
}

function buildLifeUploadDocAttachmentsPayload(documents: Record<string, any>) {
  return Object.entries(documents || {}).reduce<Record<string, unknown>[]>(
    (attachments, [fieldKey, document]) => {
      if (!document) {
        return attachments;
      }

      attachments.push({
        docName: document.fileName || document.filename || "uploaded_document",
        docType: document.mimeType || "image/jpeg",
        file:
          (document as any).file || (document as any).base64 || (document as any).fileData || "",
        attachType: toLifeAttachType(fieldKey),
      });

      return attachments;
    },
    []
  );
}

export function buildLifeCreateSessionPayload(personalData: TravelPersonalFormValues) {
  return {
    assigned_user_id: null,
    payload: {
      first_name: personalData.firstName || "",
      last_name: personalData.lastName || "",
      country_code: personalData.mobileNumber.countryCode.replace(/\D/g, ""),
      mobile_number: personalData.mobileNumber.number || "",
      email: personalData.email || "",
      partner_code: personalData.partnerCode || "",
      friends_and_family_contact: personalData.friendsAndFamilyContact || "",
      marketing_consent: personalData.marketingConsent || "no",
    },
    lob_id: LOB_IDS.LIFE,
  };
}

export function buildLifePersonalDetailsPayload(personalDetails: SharedPersonalDetailsState) {
  return {
    first_name: personalDetails.firstName || "",
    last_name: personalDetails.lastName || "",
    mobile_country_code: `+${(personalDetails.mobileNumber?.countryCode || "").replace(/\D/g, "")}`,
    mobile_number: personalDetails.mobileNumber?.number || "",
    email: personalDetails.email || "",
    partner_code: personalDetails.partnerCode || "",
    friends_and_family_contact: personalDetails.friendsAndFamilyContact || "",
    marketing_consent: personalDetails.marketingConsent || "no",
  } as const;
}

export function buildLifeInsurancePayload(termDetails: LifePlanDetailsFormValues) {
  const { startDate, endDate } = buildLifePolicyDates(termDetails);

  return {
    life_assured_date_of_birth: termDetails.lifeAssuredDob || "",
    life_assured_gender: termDetails.lifeAssuredGender || "",
    smoker: termDetails.smoker || "",
    plan_option: termDetails.planOption || "",
    sum_assured: normalizeNumericField(termDetails.sumAssured),
    policy_term: termDetails.policyTerm || "",
    premium_payment_term: normalizeNumericField(termDetails.premiumPaymentTerm),
    premium_payment_frequency: termDetails.premiumPaymentFrequency || "",
    category: termDetails.category || "",
    payout_type: termDetails.payoutType || "",
    payout_term: normalizeNumericField(termDetails.payoutTerm),
    lumpsum_percentage: normalizeNumericField(termDetails.lumpsumPercentage),
    accidental_death_sum_assured: normalizeNumericField(termDetails.accidentalDeathSumAssured),
    start_date: startDate,
    end_date: endDate,
  } as const;
}

export function buildLifeQuoteRequestBody(
  personalDetails: SharedPersonalDetailsState,
  termDetails: LifePlanDetailsFormValues,
  authSession: unknown
): LifeQuoteRequestBody {
  const { clientId, sessionId } = getAuthSessionIdentifiers(authSession);

  return {
    personal_details: buildLifePersonalDetailsPayload(personalDetails),
    life_insurance: buildLifeInsurancePayload(termDetails),
    client_id: normalizeNullableNumber(clientId),
    session_id: normalizeNullableNumber(sessionId),
    lob_id: LOB_IDS.LIFE,
  };
}

export function buildLifeSelectedQuotePayload(quote: any) {
  const commission = quote?.plan?.commission || quote?.commission;
  const resolvedPlanId = quote?.plan?.id || quote?.plan?.plan_id || quote?.plan_id || quote?.id;

  return {
    insurer_name: quote?.insurer?.name || "",
    insurer_id: quote?.insurer?.insurer_id || quote?.insurer?.id || null,
    insurer_logo: quote?.insurer?.insurer_logo || null,
    plan_name: quote?.plan?.name || "",
    plan_id: resolvedPlanId || null,
    premium: quote?.plan?.price?.amount ?? null,
    vat: quote?.plan?.price?.vat ?? null,
    total_premium: quote?.plan?.price?.amount_with_vat ?? null,
    currency: quote?.plan?.price?.currency || null,
    sections: quote?.sections || [],
    selected_addOns:
      quote?.sections
        ?.find((section: any) => section?.title === "Add-Ons")
        ?.items?.filter((item: any) => item?.is_checked === true)
        ?.map((item: any) => item?.label) || [],
    ...(commission
      ? {
          commission: {
            percentage: commission?.percentage ?? null,
            amount: commission?.amount ?? null,
            currency: commission?.currency || null,
          },
        }
      : {}),
  };
}

export function buildLifeBindPayload(params: {
  quote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  personalDetails: SharedPersonalDetailsState;
  termDetails: LifePlanDetailsFormValues;
  additionalInfo: any;
}) {
  const { quote, clientId, sessionId, personalDetails, termDetails, additionalInfo } = params;

  return {
    client_id: normalizeNullableNumber(clientId),
    session_id: normalizeNullableNumber(sessionId),
    lob_id: LOB_IDS.LIFE,
    plan_name: buildLifePlanName(quote),
    insurerId: String(quote?.insurer?.insurer_id ?? ""),
    action: "NEXT_BUTTON",
    insurerPlan: {
      id: String(quote?.plan?.id || quote?.plan?.plan_id || quote?.plan_id || quote?.id || ""),
      planName: quote?.plan?.name || "",
    },
    selectedOptionalCovers: buildLifeSelectedOptionalCovers(additionalInfo),
    travellers_more_info: buildLifeTravellerMoreInfo(personalDetails, termDetails, additionalInfo),
    declarations: {
      isUaeResidentConfirmed: additionalInfo?.uaeResidentConfirm ?? true,
    },
    attachments: [],
  };
}

export function buildLifeBuyNowBindPayload(params: {
  quote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  personalDetails: SharedPersonalDetailsState;
  termDetails: LifePlanDetailsFormValues;
  additionalInfo: any;
}) {
  const payload = buildLifeBindPayload(params);

  return {
    ...payload,
    action: "BUY",
  };
}

export function buildLifeUploadDocPayload(params: {
  quote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  personalDetails: SharedPersonalDetailsState;
  termDetails: LifePlanDetailsFormValues;
  additionalInfo: any;
  documents: Record<string, any>;
  lob_id?: number;
}) {
  const {
    quote,
    clientId,
    sessionId,
    personalDetails,
    termDetails,
    additionalInfo,
    documents,
    lob_id,
  } = params;

  return {
    client_id: normalizeNullableNumber(clientId),
    session_id: normalizeNullableNumber(sessionId),
    lob_id: lob_id ?? LOB_IDS.LIFE,
    plan_name: buildLifePlanName(quote),
    insurerId: String(quote?.insurer?.insurer_id ?? ""),
    action: "UPLOAD_DOC",
    insurerPlan: {
      id: String(quote?.plan?.id || quote?.plan?.plan_id || quote?.plan_id || quote?.id || ""),
      planName: quote?.plan?.name || "",
    },
    selectedOptionalCovers: buildLifeSelectedOptionalCovers(additionalInfo),
    travellers_more_info: buildLifeTravellerMoreInfo(personalDetails, termDetails, additionalInfo),
    declarations: {
      isUaeResidentConfirmed: additionalInfo?.uaeResidentConfirm ?? true,
    },
    attachments: buildLifeUploadDocAttachmentsPayload(documents),
  };
}

export function buildLifeAttachmentsPayload(documents: LifeUploadedDocumentMap) {
  return Object.entries(documents || {}).reduce<Record<string, unknown>>(
    (accumulator, [fieldKey, document]) => {
      if (!document) {
        return accumulator;
      }

      accumulator[fieldKey] = {
        docName: document.fileName || document.filename,
        docType: document.mimeType || "",
        doc_url:
          (document.uploadResponse as { payload?: { final_filename?: string } } | undefined)
            ?.payload?.final_filename || "",
        size: document.size || 0,
        uploadedAt: document.uploadedAt,
      };

      return accumulator;
    },
    {}
  );
}

export function buildLifePaymentPayload(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  paymentData: TravelPaymentValues;
}) {
  const { selectedQuote, clientId, sessionId, paymentData } = params;
  const amountWithVat =
    Number(selectedQuote?.plan?.price?.amount_with_vat) ||
    Number(selectedQuote?.plan?.price?.amount) ||
    0;

  return {
    client_id: clientId ? Number(clientId) : null,
    quote_id: sessionId ? Number(sessionId) : 0,
    currency: String(
      selectedQuote?.plan?.price?.currency || LOCALISATION_CONFIG.currencyCode
    ).toLowerCase(),
    payment_method: "card",
    amount: amountWithVat,
    plan_name: selectedQuote?.plan?.name || "",
    provider:
      selectedQuote?.insurer?.name && selectedQuote?.plan?.name
        ? `${selectedQuote.insurer.name} - ${selectedQuote.plan.name}`
        : selectedQuote?.insurer?.name || "Unknown",
    card_number: paymentData.cardNumber.replace(/\s/g, ""),
    card_expiry: paymentData.expiry,
    card_cvv: paymentData.cvv,
    card_name: paymentData.cardHolder,
  };
}

export function buildLifeIssuePolicyPayload(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  paymentResponse: any;
  lobId?: number;
}) {
  const { selectedQuote, clientId, sessionId, paymentResponse, lobId } = params;
  const paymentPayload = getPaymentResponsePayload(paymentResponse);

  return {
    insurerId: Number(selectedQuote?.insurer?.insurer_id ?? 0) || null,
    action: "ISSUE_POLICY",
    client_id: clientId ? Number(clientId) : null,
    session_id: sessionId ? Number(sessionId) : null,
    policy_issued_by: "retail",
    plan_name: paymentPayload?.plan_name || selectedQuote?.plan?.name || "",
    provider_transaction_id:
      paymentPayload?.provider_transaction_id || paymentPayload?.transaction_id || "",
    lob_id: lobId ?? LOB_IDS.LIFE,
  };
}

export const normalizePropertyText = (text: string): string => {
  if (!text) return "";

  return (
    text
      // camelCase/PascalCase -> add space
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      // snake_case / kebab-case -> replace with space
      .replace(/[_-]+/g, " ")
      // remove extra spaces
      .replace(/\s+/g, " ")
      .trim()
      // Title Case
      .replace(/\b\w/g, (char) => char.toUpperCase())
  );
};
